import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, CircularProgress, Typography, Alert, Grid, Paper } from '@mui/material';
import { ChallengeTableInspectionsProgramsVersionsItem, ChallengeTableProgramVersionItem } from '../../api/types/dtos/challenge/ChallengeDto';
import { Form, FormAnswers } from '../../api/types/forms';
import DynamicFormRenderer from '../forms/DynamicFormRenderer';
import FormAnswersViewer from '../forms/FormAnswersViewer';
import { useOrganization } from '../../contexts/OrganizationContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { answerService, AnswerDto } from '../../api/services/answer.service';
import { formService, FormDto } from '../../api/services/form.service';

// Define the type for the exposed submit function from DynamicFormRenderer
interface DynamicFormRendererHandle {
  submit: () => void;
}

// Interface for tracking individual item state
interface BatchEditItemState {
  item: ChallengeTableInspectionsProgramsVersionsItem;
  formTemplate?: string;
  programVersionItem?: ChallengeTableProgramVersionItem;
  parsedFormDefinition?: Form | null;
  initialFormAnswers?: FormAnswers | null;
  displayFormRendererMode: boolean;
  isLoadingTemplate: boolean;
  isLoadingAnswers: boolean;
  needsFormFetch: boolean; // True if formId exists and we need to fetch the form definition
  isSubmitting: boolean;
  isFileUploading: boolean;
  isExpanded: boolean;
  isValid: boolean;
  error?: string;
  answersSaved?: boolean; // Track if answers have been saved via API for new system
}

interface InspectionRowDetailsModalProps {
  open: boolean;
  onClose: () => void;
  items: ChallengeTableInspectionsProgramsVersionsItem[];
  onSave: (updated: ChallengeTableInspectionsProgramsVersionsItem[]) => void;
  programVersionItems?: ChallengeTableProgramVersionItem[];
  ownerName?: string;
  farmName?: string;
  phoneNumber?: string;
}

const InspectionRowDetailsModal: React.FC<InspectionRowDetailsModalProps> = ({
  open,
  onClose,
  items,
  onSave,
  programVersionItems = [],
  ownerName,
  farmName,
  phoneNumber,
}) => {
  const [itemStates, setItemStates] = useState<BatchEditItemState[]>([]);

  // Helper to create state for a single item
  const createItemState = useCallback((item: ChallengeTableInspectionsProgramsVersionsItem, programVersionItem?: ChallengeTableProgramVersionItem, isFirst = false): BatchEditItemState => {
    const formTemplate = programVersionItem?.measurementTemplate;
    const hasFormId = !!programVersionItem?.formId;

    let initialValue = item.value;
    // Only set default date value if there's no form
    if (!hasFormId && !formTemplate && (initialValue === null || initialValue === undefined || initialValue === '')) {
      initialValue = new Date().toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
    }

    return {
      item: { ...item, value: initialValue },
      formTemplate,
      programVersionItem,
      parsedFormDefinition: null, // Will be fetched if formId exists
      initialFormAnswers: null, // Will be fetched if formId exists
      displayFormRendererMode: hasFormId || !!formTemplate, // Show form renderer if formId or template exists
      isLoadingTemplate: hasFormId, // Loading if we need to fetch the form
      isLoadingAnswers: hasFormId, // Loading if we need to fetch answers
      needsFormFetch: hasFormId, // True if we need to fetch form definition
      isSubmitting: false,
      isFileUploading: false,
      isExpanded: isFirst,
      isValid: true,
      answersSaved: false
    };
  }, []);

  // Keep ref in sync...
  const submitRefs = useRef<{ [itemId: string]: DynamicFormRendererHandle | null }>({});
  const itemStatesRef = useRef<BatchEditItemState[]>([]);
  const { selectedOrganization } = useOrganization();

  // Keep ref in sync with state
  useEffect(() => {
    itemStatesRef.current = itemStates;
  }, [itemStates]);

  // Track the last initialized items to prevent re-initialization on same data
  const lastInitializedRef = useRef<{ open: boolean; itemIds: string[] }>({ open: false, itemIds: [] });

  // Reset all state when modal closes to ensure fresh data on next open
  useEffect(() => {
    if (!open) {
      // Clear all state when modal closes
      setItemStates([]);
      submitRefs.current = {};
      lastInitializedRef.current = { open: false, itemIds: [] };
    }
  }, [open]);

  // Initialize item states when modal opens or items change
  useEffect(() => {
    if (!open || items.length === 0) {
      return;
    }

    // Create a stable key from current items
    const currentItemIds = items.map(i => i.id).sort().join(',');
    const wasOpen = lastInitializedRef.current.open;
    const lastItemIds = lastInitializedRef.current.itemIds.join(',');

    // Only reinitialize if modal just opened or item IDs actually changed
    const shouldInitialize = !wasOpen || currentItemIds !== lastItemIds;

    if (!shouldInitialize) {
      return;
    }

    // Update tracking
    lastInitializedRef.current = { open: true, itemIds: items.map(i => i.id).sort() };

    // First, group items by program level and sort them
    const groupedByLevel: { [levelName: string]: { item: ChallengeTableInspectionsProgramsVersionsItem; programVersionItem?: ChallengeTableProgramVersionItem }[] } = {};

    items.forEach(item => {
      const programVersionItem = programVersionItems.find(pvi => pvi.id === item.programVersionItemId);
      const levelName = programVersionItem?.programLevelName || 'General';

      if (!groupedByLevel[levelName]) {
        groupedByLevel[levelName] = [];
      }

      groupedByLevel[levelName].push({ item, programVersionItem });
    });

    // Sort items within each level by cpNumber
    for (const levelName in groupedByLevel) {
      groupedByLevel[levelName].sort((a, b) => {
        const cpA = a.programVersionItem?.cpNumber || '';
        const cpB = b.programVersionItem?.cpNumber || '';
        return cpA.localeCompare(cpB, undefined, { numeric: true });
      });
    }

    // Create sorted list of levels
    const sortedLevels = Object.entries(groupedByLevel).sort(([levelA], [levelB]) =>
      levelA.localeCompare(levelB)
    );

    // Flatten the sorted and grouped items back into a single array
    const sortedItems: { item: ChallengeTableInspectionsProgramsVersionsItem; programVersionItem?: ChallengeTableProgramVersionItem }[] = [];
    sortedLevels.forEach(([, levelItems]) => {
      sortedItems.push(...levelItems);
    });

    // Initialize fresh states
    const newStates = sortedItems.map(({ item, programVersionItem }, index) =>
      createItemState(item, programVersionItem, index === 0)
    );

    setItemStates(newStates);
  }, [open, items, programVersionItems, createItemState]);

  // Fetch forms and answers from the new system for items with formId
  useEffect(() => {
    if (!open || !selectedOrganization || itemStates.length === 0) {
      return;
    }

    // Find items that have formId and need form/answers fetched
    const itemsNeedingFetch = itemStates.filter(
      state => state.programVersionItem?.formId && state.needsFormFetch
    );

    if (itemsNeedingFetch.length === 0) {
      return;
    }

    // Fetch form definition and answers for each item in parallel
    itemsNeedingFetch.forEach(state => {
      const { item, programVersionItem } = state;
      Promise.all([
        formService.getById(selectedOrganization.id, programVersionItem!.formId!),
        answerService.getAnswers(
          selectedOrganization.id,
          item.inspectionProgramId,
          item.programVersionId,
          item.id
        )
      ]).then(([formResponse, answersResponse]: [any, any]) => {
        // Convert FormDto to Form interface for DynamicFormRenderer
        const formDto: FormDto = formResponse.data;
        const formDef: Form = {
          form_id: formDto.id,
          organizationId: formDto.organizationId,
          stringId: formDto.stringId,
          connector: formDto.connector,
          title: formDto.title,
          description: formDto.description,
          questions: formDto.questions.map(q => ({
            id: q.stringId, // Use stringId as legacy ID for field names & condition lookups
            uuid: q.id, // Use backend UUID for answer storage
            text: q.text,
            help_text: q.helpText,
            type: q.type.toLowerCase() as any, // Convert MULTISELECT -> multiselect
            options: q.options,
            mandatory: q.mandatory,
            placeholder: q.placeholder,
            default_value: q.defaultValue,
            validation: q.validation ? JSON.parse(q.validation) : undefined,
            display_logic: {
              condition_type: q.displayLogicConditionType.toLowerCase() as any,
              conditions: q.displayLogicConditions ? JSON.parse(q.displayLogicConditions) : undefined,
              source_multiselect_id: q.displayLogicSourceMultiselectId,
              context_label_template: q.displayLogicContextLabelTemplate
            },
            attachments: []
          })) as any
        };

        // Process answers - parse JSON values if they're strings
        const answersData = answersResponse.data || [];
        const mappedAnswers: FormAnswers = answersData.map((a: AnswerDto) => {
          let parsedValue = a.value;
          // If value is a string that looks like JSON, parse it
          if (typeof parsedValue === 'string' && (parsedValue.startsWith('[') || parsedValue.startsWith('{'))) {
            try {
              parsedValue = JSON.parse(parsedValue);
            } catch {
              // Keep original value if parsing fails
            }
          }
          return {
            answerId: a.id,
            questionId: a.questionId,
            value: parsedValue,
            forEachContextValue: a.forEachContextValue,
            parentContextId: a.parentContextId
          };
        });

        // Update the item state with fetched form and answers
        updateItemState(item.id, {
          parsedFormDefinition: formDef,
          initialFormAnswers: mappedAnswers,
          isLoadingTemplate: false,
          isLoadingAnswers: false,
          needsFormFetch: false
        });
      }).catch(error => {
        console.error(`Failed to fetch form or answers for item ${item.id}:`, error);
        // Fall back to legacy template if available
        const formTemplate = state.programVersionItem?.measurementTemplate;
        if (formTemplate) {
          try {
            const parsedDef = JSON.parse(formTemplate) as Form;
            updateItemState(item.id, {
              parsedFormDefinition: parsedDef,
              isLoadingTemplate: false,
              needsFormFetch: false
            });
          } catch (e) {
            console.error('Failed to parse fallback formTemplate:', e);
            updateItemState(item.id, {
              displayFormRendererMode: false,
              isLoadingTemplate: false,
              needsFormFetch: false
            });
          }
        } else {
          updateItemState(item.id, {
            displayFormRendererMode: false,
            isLoadingTemplate: false,
            needsFormFetch: false
          });
        }
      });
    });
  }, [open, selectedOrganization, itemStates]);

  const updateItemState = useCallback((itemId: string, updates: Partial<BatchEditItemState>) => {
    setItemStates(prev => prev.map(state =>
      state.item.id === itemId ? { ...state, ...updates } : state
    ));
  }, []);

  const handleBasicFieldChange = useCallback((itemId: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let newValue: any = value;
    if (type === 'checkbox' && 'checked' in e.target) {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setItemStates(prev => prev.map(state => {
      if (state.item.id !== itemId) return state;
      return {
        ...state,
        item: { ...state.item, [name]: newValue }
      };
    }));
  }, []);

  const handleStateSelectChange = useCallback((itemId: string, value: string) => {
    const currentState = itemStatesRef.current.find(s => s.item.id === itemId);
    if (!currentState) return;

    const nowIso = new Date().toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
    const prevIsActive = currentState.item.isActive;

    if (value === 'pending') {
      updateItemState(itemId, {
        item: { ...currentState.item, isActive: false, isApproved: false }
      });
    } else if (value === 'inProgress') {
      updateItemState(itemId, {
        item: {
          ...currentState.item,
          isActive: true,
          isApproved: false,
          startDate: currentState.item.startDate ? currentState.item.startDate : nowIso
        }
      });
    } else if (value === 'completed') {
      if (!prevIsActive) {
        // From Pendiente to Completado: set both start and end date if empty
        updateItemState(itemId, {
          item: {
            ...currentState.item,
            isActive: true,
            isApproved: true,
            startDate: currentState.item.startDate ? currentState.item.startDate : nowIso,
            endDate: currentState.item.endDate ? currentState.item.endDate : nowIso
          }
        });
      } else {
        // From En Proceso to Completado: set only end date if empty
        updateItemState(itemId, {
          item: {
            ...currentState.item,
            isActive: true,
            isApproved: true,
            endDate: currentState.item.endDate ? currentState.item.endDate : nowIso
          }
        });
      }
    }
  }, [updateItemState]);

  const handleStartDateChange = useCallback((itemId: string, date: Date | null) => {
    const item = itemStatesRef.current.find(s => s.item.id === itemId)?.item;
    if (!item) return;
    updateItemState(itemId, {
      item: { ...item, startDate: date ? new Date(date.setSeconds(0, 0)).toISOString().replace(/\.(\d{3})Z$/, ".$1000Z") : null }
    });
  }, [updateItemState]);

  const handleEndDateChange = useCallback((itemId: string, date: Date | null) => {
    const item = itemStatesRef.current.find(s => s.item.id === itemId)?.item;
    if (!item) return;
    updateItemState(itemId, {
      item: { ...item, endDate: date ? new Date(date.setSeconds(0, 0)).toISOString().replace(/\.(\d{3})Z$/, ".$1000Z") : null }
    });
  }, [updateItemState]);

  const handleAccordionChange = useCallback((itemId: string, isExpanded: boolean) => {
    updateItemState(itemId, { isExpanded });
  }, [updateItemState]);

  const processAndSaveDynamicForm = useCallback(async (itemId: string, answers: FormAnswers) => {
    const currentState = itemStatesRef.current.find(s => s.item.id === itemId);
    if (!currentState || !selectedOrganization) return;

    // If using new forms system (formId exists), save to answers table
    if (currentState.programVersionItem?.formId) {
      setItemStates(prev => prev.map(state => {
        if (state.item.id !== itemId) return state;
        return { ...state, isSubmitting: true };
      }));

      try {
        // Convert FormAnswers to CreateAnswerRequest format
        const createAnswerRequests = answers.map(ans => ({
          questionId: ans.questionId,
          value: ans.value,
          forEachContextValue: ans.forEachContextValue,
          parentContextId: ans.parentContextId
        }));

        await answerService.saveAnswers(
          selectedOrganization.id,
          currentState.item.inspectionProgramId,
          currentState.item.programVersionId,
          currentState.item.id,
          createAnswerRequests
        );

        // Mark as saved, keep state for other fields (comments, dates, etc.)
        setItemStates(prev => prev.map(state => {
          if (state.item.id !== itemId) return state;
          return { ...state, isSubmitting: false, answersSaved: true };
        }));
      } catch (error) {
        console.error('Error saving to answers table:', error);
        setItemStates(prev => prev.map(state => {
          if (state.item.id !== itemId) return state;
          return { ...state, isSubmitting: false, error: 'Error saving answers' };
        }));
      }
    } else {
      // Legacy system: save to item.value
      setItemStates(prev => prev.map(state => {
        if (state.item.id !== itemId) return state;
        try {
          const initialAnswers = state.item.value ? JSON.parse(state.item.value) : [];

          // MERGE STRATEGY: Preserve all initial answers and update/add only what changed
          const newAnswersMap = new Map<string, any>();
          answers.forEach(ans => {
            const key = `${ans.questionId}::${ans.forEachContextValue || 'root'}`;
            newAnswersMap.set(key, ans);
          });

          const mergedAnswers: FormAnswers = [];
          const processedKeys = new Set<string>();

          // Update existing answers or keep them unchanged
          initialAnswers.forEach((initialAns: any) => {
            const key = `${initialAns.questionId}::${initialAns.forEachContextValue || 'root'}`;
            processedKeys.add(key);

            if (newAnswersMap.has(key)) {
              mergedAnswers.push(newAnswersMap.get(key));
            } else {
              mergedAnswers.push(initialAns);
            }
          });

          // Add any completely new answers
          answers.forEach(ans => {
            const key = `${ans.questionId}::${ans.forEachContextValue || 'root'}`;
            if (!processedKeys.has(key)) {
              mergedAnswers.push(ans);
            }
          });

          const stringifiedAnswers = JSON.stringify(mergedAnswers);

          return {
            ...state,
            item: { ...state.item, value: stringifiedAnswers },
            isSubmitting: false
          };
        } catch (e) {
          console.error('Error processing form answers:', e);
          return { ...state, isSubmitting: false, error: 'Error saving form data' };
        }
      }));
    }
  }, [selectedOrganization, updateItemState]);

  const handleSaveAll = async () => {
    // Check if any items are still submitting or have file uploads in progress
    const hasActiveUploads = itemStates.some(state => state.isSubmitting || state.isFileUploading);
    if (hasActiveUploads) {
      return;
    }

    // Submit all dynamic forms
    for (const state of itemStates) {
      if (state.displayFormRendererMode && submitRefs.current[state.item.id]) {
        try {
          submitRefs.current[state.item.id]?.submit();
        } catch (error) {
          console.error(`Error submitting form for item ${state.item.id}:`, error);
        }
      }
    }

    // Wait for async answer saves to complete (for items with formId)
    // We poll the state to check when all items with formId have answersSaved = true
    const maxWaitTime = 10000; // 10 seconds max wait
    const startTime = Date.now();

    const waitForAnswerSaves = () => new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        const latest = itemStatesRef.current;
        const itemsWithFormId = latest.filter(s => s.programVersionItem?.formId);
        const allSaved = itemsWithFormId.every(s => s.answersSaved || s.error);

        if (allSaved || Date.now() - startTime > maxWaitTime) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });

    await waitForAnswerSaves();

    // Save all items
    const latest = itemStatesRef.current;
    const allItems = latest.map(state => state.item);

    if (allItems.length > 0) {
      onSave(allItems);
    }
    onClose();
  };

  const handleFileUploadingChange = useCallback((itemId: string, isUploading: boolean) => {
    updateItemState(itemId, { isFileUploading: isUploading });
  }, [updateItemState]);

  const hasAnyUploading = itemStates.some(state => state.isFileUploading || state.isSubmitting);

  // Memoize grouping logic to avoid recomputation on every render
  const groupedStates = useMemo(() => {
    const grouped: { [levelName: string]: BatchEditItemState[] } = {};

    itemStates.forEach(state => {
      const levelName = state.programVersionItem?.programLevelName || 'General';
      if (!grouped[levelName]) {
        grouped[levelName] = [];
      }
      grouped[levelName].push(state);
    });

    // Sort levels alphabetically
    return Object.entries(grouped).sort(([levelA], [levelB]) =>
      levelA.localeCompare(levelB)
    );
  }, [itemStates]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Only close via Cancel or Save buttons, not backdrop click or escape
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            {farmName && (
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', fontSize: '1.5rem', mb: 0.5 }}>
                {farmName}
              </Typography>
            )}
            <Box sx={{ mt: 0.5 }}>
              {ownerName && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'block' }}>
                  {ownerName}
                </Typography>
              )}
              {phoneNumber && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'block' }}>
                  {phoneNumber}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {groupedStates.map(([levelName, levelStates]) => (
          <Box key={levelName} mb={3}>

              {/* Items in this level */}
              {levelStates.map((state) => {
                const { item, programVersionItem, displayFormRendererMode, parsedFormDefinition,
                  initialFormAnswers, isLoadingTemplate, isLoadingAnswers, isSubmitting, isFileUploading,
                  isExpanded, error } = state;

                return (
                  <Accordion
                    key={item.id}
                    expanded={isExpanded}
                    onChange={(_, expanded) => handleAccordionChange(item.id, expanded)}
                    sx={{
                      border: '1px solid #e0e0e0',
                      '&:before': { display: 'none' },
                      boxShadow: 1,
                      mb: 1
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            backgroundColor: item.isApproved ? '#6fbf73' : item.isActive ? '#ffa726' : '#9e9e9e',
                            border: `2px solid ${item.isApproved ? '#6fbf73' : item.isActive ? '#ffa726' : '#9e9e9e'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            color: '#fff',
                            flexShrink: 0,
                          }}
                        >
                          {programVersionItem?.cpNumber?.split('.').pop() || '?'}
                        </Box>
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {programVersionItem?.name || `Item ${item.programVersionItemId}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {programVersionItem?.cpNumber}
                          </Typography>
                        </Box>
                        {(isSubmitting || isFileUploading) && (
                          <CircularProgress size={20} />
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {error}
                        </Alert>
                      )}

                      {/* Description */}
                      {programVersionItem?.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {programVersionItem.description}
                        </Typography>
                      )}

                      {/* Comments Field */}
                      <TextField
                        label="Comentarios"
                        name="comments"
                        value={item.comments || ''}
                        onChange={(e) => handleBasicFieldChange(item.id, e)}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={2}
                        disabled={isSubmitting}
                      />
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                        <Grid container spacing={2} sx={{ marginTop: '8px !important', marginBottom: '8px !important' }}>
                          <Grid item xs={12} sm={4}>
                            <DatePicker
                              label="Fecha de inicio"
                              value={item.startDate ? new Date(item.startDate) : null}
                              onChange={(date) => handleStartDateChange(item.id, date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  variant: 'outlined',
                                  placeholder: '',
                                  sx: {
                                    '& .MuiInputLabel-root': { fontSize: '14px !important' },
                                    '& .MuiInputBase-input': { fontSize: '14px !important' }
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <DatePicker
                              label="Fecha de finalización"
                              value={item.endDate ? new Date(item.endDate) : null}
                              onChange={(date) => handleEndDateChange(item.id, date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  variant: 'outlined',
                                  placeholder: '',
                                  sx: {
                                    '& .MuiInputLabel-root': { fontSize: '14px !important' },
                                    '& .MuiInputBase-input': { fontSize: '14px !important' }
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel id={`state-select-label-${item.id}`} sx={{ fontSize: '14px !important' }}>Estado</InputLabel>
                              <Select
                                labelId={`state-select-label-${item.id}`}
                                value={
                                  !item.isActive ? 'pending' : item.isApproved ? 'completed' : 'inProgress'
                                }
                                label="Estado"
                                onChange={(e) => handleStateSelectChange(item.id, e.target.value)}
                                disabled={isSubmitting}
                                sx={{ fontSize: '14px !important' }}
                              >
                                <MenuItem value="pending">Pendiente</MenuItem>
                                <MenuItem value="inProgress">En Proceso</MenuItem>
                                <MenuItem value="completed">Completado</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </LocalizationProvider>

                      {/* Value Field - Dynamic Form or Simple Text */}
                      {isLoadingTemplate || isLoadingAnswers ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                          <CircularProgress />
                        </Box>
                      ) : displayFormRendererMode && parsedFormDefinition ? (
                        <Paper
                          elevation={2}
                          sx={{
                            marginTop: '24px !important',
                            padding: '24px !important',
                            backgroundColor: '#f8f9fa !important',
                            borderLeft: '4px solid #1976d2 !important',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0 !important'
                          }}
                        >
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: '600 !important',
                              color: '#1976d2 !important',
                              marginBottom: '16px !important',
                              paddingBottom: '8px !important',
                              borderBottom: '2px solid #e0e0e0 !important'
                            }}
                          >
                            {parsedFormDefinition.title}
                          </Typography>
                          <DynamicFormRenderer
                            key={`form-${item.id}`}
                            ref={(ref) => { submitRefs.current[item.id] = ref; }}
                            formDefinition={parsedFormDefinition}
                            initialAnswers={initialFormAnswers}
                            onSubmit={(answers) => processAndSaveDynamicForm(item.id, answers)}
                            onCancel={onClose}
                            organizationId={selectedOrganization?.id || ''}
                            programId={item.programVersionId || ''}
                            inspectionProgramId={item.inspectionProgramId || ''}
                            itemId={item.id || item.programVersionItemId || ''}
                            onUploadingChange={(isUploading) => handleFileUploadingChange(item.id, isUploading)}
                          />
                        </Paper>
                      ) : (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            label="Valor"
                            name="value"
                            value={item.value || ''}
                            onChange={(e) => handleBasicFieldChange(item.id, e)}
                            fullWidth
                            margin="normal"
                            multiline={typeof item.value === 'string' && item.value.length > 50}
                            disabled={isSubmitting}
                          />

                          {/* Show FormAnswersViewer if there's a value but no dynamic form */}
                          {item.value && programVersionItem?.measurementTemplate && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Vista de Respuestas:
                              </Typography>
                              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <FormAnswersViewer
                                  formTemplateJson={programVersionItem.measurementTemplate}
                                  formAnswersJson={item.value}
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={hasAnyUploading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSaveAll}
          variant="contained"
          color="primary"
          disabled={hasAnyUploading}
        >
          {hasAnyUploading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Procesando...
            </>
          ) : (
            'Guardar Todos'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InspectionRowDetailsModal;